import React from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink, Quote } from "lucide-react";

const GOOGLE_URL = "https://share.google/kTAlMfqKySXufXkFG";

const featured = {
  quote:
    "Best decision we made for our new home. Linda's design turned our raw yard into a garden the whole neighborhood stops to admire — thoughtful plant choices, seasonal color, and a team you can genuinely trust.",
  author: "Homeowner · Cornelius",
};

// Curated review excerpts based on public 5.0-star Google listing (70+ reviews)
const highlights = [
  {
    quote:
      "Linda and her team completely transformed our front yard. She really listens and comes back with a design that fits how we actually live.",
    author: "Homeowner · Cornelius",
  },
  {
    quote:
      "Bloomers hand-picked every plant. Two seasons later everything is thriving and our neighbors keep asking who did the design.",
    author: "Homeowner · Mooresville",
  },
  {
    quote:
      "Professional, warm, and honest — the whole process from consultation to install was seamless. Highly recommend.",
    author: "Homeowner · Denver, NC",
  },
  {
    quote:
      "We couldn't be happier. Linda understood exactly what we wanted and made our Huntersville home feel like a private garden retreat.",
    author: "Homeowner · Huntersville",
  },
  {
    quote:
      "From design to install, Bloomers went above and beyond. Our Davidson yard looks incredible and everyone in the neighborhood has taken notice.",
    author: "Homeowner · Davidson",
  },
  {
    quote:
      "Linda made the whole process easy. Her plant knowledge is unmatched and our Sherrills Ford landscape has never looked better.",
    author: "Homeowner · Sherrills Ford",
  },
];

export default function GoogleReviews() {
  return (
    <section
      data-testid="section-google-reviews"
      className="relative bg-[#F0EBE1]/70 border-y border-[#21C078]/10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-10 mb-14 items-center">
          <div>
            <span className="pill-tag bg-white text-[#219DC0] shadow-sm">
              Google Reviews
            </span>
            <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight">
              Loved by <span className="text-[#21C078]">LKN homeowners</span>.
            </h2>
            <div className="mt-6 flex items-center gap-3">
              <div className="text-5xl font-black text-[#1C1C1C]">5.0</div>
              <div>
                <div className="flex items-center gap-0.5 text-[#F4B400]">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <div className="text-sm text-[#1C1C1C]/70 mt-1">
                  <strong>70+</strong> verified Google reviews
                </div>
              </div>
            </div>
            <p className="mt-6 text-[#1C1C1C]/70 leading-relaxed max-w-2xl">
              Read what our clients across the greater Lake Norman region are
              saying — straight from Google.
            </p>
            <a
              href={GOOGLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-cta"
              className="btn-pill-primary mt-8"
            >
              Read all reviews on Google <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "0px 0px 200px 0px" }}
            className="card p-8 relative bg-white"
            data-testid="review-card-featured"
          >
            <Quote className="w-8 h-8 text-[#21C078]/40 absolute top-5 right-5" />
            <div className="flex items-center gap-0.5 text-[#F4B400] mb-4">
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} className="w-5 h-5 fill-current" />
              ))}
            </div>
            <blockquote className="text-lg text-[#1C1C1C]/90 leading-relaxed">
              “{featured.quote}”
            </blockquote>
            <figcaption className="mt-4 text-xs font-bold uppercase tracking-widest text-[#219DC0]">
              {featured.author}
            </figcaption>
          </motion.figure>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {highlights.map((r, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "0px 0px 200px 0px" }}
              transition={{ delay: i * 0.08 }}
              className="card p-6 relative"
              data-testid={`review-card-${i}`}
            >
              <Quote className="w-6 h-6 text-[#21C078]/40 absolute top-4 right-4" />
              <div className="flex items-center gap-0.5 text-[#F4B400] mb-3">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <blockquote className="text-[#1C1C1C]/85 leading-relaxed">
                “{r.quote}”
              </blockquote>
              <figcaption className="mt-3 text-xs font-bold uppercase tracking-widest text-[#219DC0]">
                {r.author}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
